import { Page } from 'components/Page/Page';
import { useCallback, useEffect, useRef, useState } from 'react';
import i18n from 'i18n';
import { SiteAnnouncementListColumn, SiteAnnouncementListModel, SIZE_PER_PAGE, useSiteAnnouncementListModel } from './SiteAnnouncementListModel';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { StickableBootstrapTable } from 'components/Table/StickableBootstrapTable';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { SiteAnnouncement, SiteAnnouncementStatus, SiteAnnouncementType } from 'core/siteAnnouncement/SiteAnnouncement';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { faPencilAlt, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { Modal } from 'components/Modal/Modal';
import styles from './siteAnnouncementList.module.scss';

const REFRESH_READ_STATUS_TIMEOUT = 5000; // 5 seconds

// 頁標題隨分類切換。用 Record 是為了新增分類時強制補上標題（缺一個就編不過）
const TITLE_I18N_KEY: Record<SiteAnnouncementType, string> = {
  [SiteAnnouncementType.SYSTEM]: 'siteAnnouncementList.titles.system',
  [SiteAnnouncementType.PROMOTION]: 'siteAnnouncementList.titles.promotion'
};

export type SiteAnnouncementListProps = {
  type: SiteAnnouncementType;
};

export const SiteAnnouncementList: React.FC<SiteAnnouncementListProps> = ({ type }) => {

  const location = useLocation<{ autoOpenAnnouncementId?: number } | undefined>();
  const history = useHistory();
  const autoOpenAnnouncementId = location.state?.autoOpenAnnouncementId;

  const {
    loading,
    isAdmin,
    searchString,
    filteredAnnouncements,
    selectedAnnouncements,
    columns,
    batchOperations,
    onPageChange,
    onHandleSearch,
    onHandleSelect,
    onHandleSelectPage,
    onClearSelect,
    markAnnouncementsAsRead,
    getAnnouncementRowClasses,
    showDeleteConfirmModal,
    onShowDeleteConfirmModal,
    onHideDeleteConfirmModal,
    onConfirmDelete
  }: SiteAnnouncementListModel = useSiteAnnouncementListModel(type, autoOpenAnnouncementId);

  const readTimeoutRef = useRef<NodeJS.Timeout>();
  const [, refreshReadStatus] = useState(false);

  useEffect(() => {
    const handler = () => refreshReadStatus(prev => !prev);
    window.addEventListener('announcements-read', handler);
    return () => window.removeEventListener('announcements-read', handler);
  }, []);

  useEffect(() => {
    refreshReadStatus(false);
    let readTimeoutId = readTimeoutRef.current;
    readTimeoutId && clearTimeout(readTimeoutId);

    readTimeoutId = setTimeout(() => {
      markAnnouncementsAsRead();
      refreshReadStatus(true);
      history.replace({ pathname: location.pathname, state: undefined });
    }, REFRESH_READ_STATUS_TIMEOUT);

    readTimeoutRef.current = readTimeoutId;
    return () => {
      readTimeoutId && clearTimeout(readTimeoutId);
    };
  }, [markAnnouncementsAsRead, history, location.pathname]);

  const renderListOperations = useCallback(() => [
    // 帶著入口的分類進新增頁：從活動優惠入口按新增，表單的通知分類就預設活動優惠
    <Link
      key='new-btn'
      to={`/site-announcements/new?type=${type}`}
      className={'btn btn-tertiary btn-sm'}
    >
      {i18n.t<string>('siteAnnouncementList.buttons.create')}
    </Link>
  ], [type]);

  const handleShowDeleteConfirmModal = useCallback((announcementId: number) => (event?: React.MouseEvent) => {
    event && event.stopPropagation();
    onShowDeleteConfirmModal([announcementId]);
  }, [onShowDeleteConfirmModal]);

  const canDeleteAnnouncement = useCallback((announcement: SiteAnnouncement) => {
    // Only allow deletion of unpublished announcements
    return announcement.status === SiteAnnouncementStatus.UNPUBLISHED;
  }, []);

  const renderRowBtns = useCallback((announcement: SiteAnnouncement) => [
    <IconWithTooltip
      key={`edit-btn-${announcement.id}`}
      icon={faPencilAlt}
      tooltipProps={{
        id: `edit-site-announcements-${announcement.id}`,
        link: `/site-announcements/${announcement.id}/edit`,
        tooltip: i18n.t<string>('siteAnnouncementList.hints.edit')
      }}
    />,
    <IconWithTooltip
      key={`delete-btn-${announcement.id}`}
      icon={faTrashAlt}
      disabled={!canDeleteAnnouncement(announcement)}
      onClick={handleShowDeleteConfirmModal(announcement.id)}
      tooltipProps={{
        id: `delete-site-announcements-${announcement.id}`,
        tooltip: canDeleteAnnouncement(announcement) ?
          i18n.t<string>('common.buttons.delete') :
          i18n.t<string>('siteAnnouncementList.hints.cannotDelete')
      }}
    />
  ], [handleShowDeleteConfirmModal, canDeleteAnnouncement]);

  // Apply useCallback & instant execution instead of useMemo to avoid unnecessary re-renders and performance issues
  const tablePropsOfRole = useCallback(() => isAdmin ? ({
    selectedRows: selectedAnnouncements,
    batchOperaionConfig: batchOperations,
    noDataIndication: i18n.t<string>('siteAnnouncementList.placeholders.adminNoData'),
    renderRowBtns: renderRowBtns,
    onSelect: onHandleSelect,
    onSelectPage: onHandleSelectPage,
    renderListOperations: renderListOperations
  }) : {
    noDataIndication: i18n.t<string>('siteAnnouncementList.placeholders.noData')
  }, [
    isAdmin,
    selectedAnnouncements,
    batchOperations,
    renderRowBtns,
    onHandleSelect,
    onHandleSelectPage,
    renderListOperations
  ])();

  return (
    <Page title={i18n.t<string>(TITLE_I18N_KEY[type])}>
      {loading && <LoadingIndicator />}
      <div className={styles.siteAnnouncementList}>
        <StickableBootstrapTable
          customPadding={false}
          keyField={SiteAnnouncementListColumn.ID}
          data={filteredAnnouncements}
          columns={columns}
          rowClasses={getAnnouncementRowClasses}
          defaultSearchString={searchString}
          searchbarPlaceholder={i18n.t<string>(
            'siteAnnouncementList.placeholders.searchbar'
          )}
          onHandleSearch={onHandleSearch}
          onRemoveAllSelect={onClearSelect}
          pagination={{
            sizePerPage: SIZE_PER_PAGE,
            onPageChange
          }}
          {...tablePropsOfRole}
        />
      </div>
      {showDeleteConfirmModal && (
        <Modal
          title={i18n.t<string>('siteAnnouncementList.labels.deleteModalTitle')}
          secondaryButton={{
            title: i18n.t<string>('common.buttons.cancel'),
            callback: onHideDeleteConfirmModal
          }}
          primaryButton={{
            title: i18n.t<string>('common.buttons.delete'),
            callback: onConfirmDelete
          }}
          dismiss={onHideDeleteConfirmModal}
        >
          {i18n.t<string>('siteAnnouncementList.labels.deleteModalContent')}
        </Modal>
      )}
    </Page>
  );
};
